import { useState, useCallback, useRef } from 'react';
import CVAnalyzerService from '../services/cvAnalyzerService';
import JobAnalyzerService from '../services/jobAnalyzerService';
import CompanyAnalyzerService from '../services/companyAnalyzerService';
import { logger } from '../utils/logger';

/**
 * Custom hook for analysis operations and state management
 * @param {string} type - The analyzer type (cv, job, company)
 * @param {function} onComplete - Callback when analysis is complete
 * @param {function} onStatusChange - Callback when status changes
 * @returns {object} Analysis operations and state
 */
export const useAnalysisOperations = (type, onComplete, onStatusChange) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [result, setResult] = useState(null);
  const [data, setData] = useState({});
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // AbortController for cancelling ongoing requests
  const abortControllerRef = useRef(null);

  /**
   * Cancel any ongoing analysis
   */
  const cancelAnalysis = useCallback(() => {
    if (abortControllerRef.current) {
      logger.info('[useAnalysisOperations]', 'Cancelling ongoing analysis');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setAnalyzing(false);
      setError('Analysis cancelled');
    }
  }, []);

  /**
   * Start analysis with provided data
   */
  const startAnalysis = useCallback(async (analysisData) => {
    logger.info('[useAnalysisOperations]', `Starting ${type} analysis`);

    // Cancel any ongoing request
    cancelAnalysis();

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setAnalyzing(true);
    setError('');

    try {
      let response;

      // Call appropriate analyzer service based on type
      switch (type) {
        case 'cv':
          response = await CVAnalyzerService.processCv(
            analysisData.text || '',
            analysisData.files || [],
            analysisData.userId || (() => { throw new Error('userId is required for CV analysis'); })(),
            analysisData.language || 'en'
          );
          break;

        case 'job':
          response = await JobAnalyzerService.processJobPosting(
            analysisData.text || '',
            analysisData.files || [],
            analysisData.userId || (() => { throw new Error('userId is required for analysis'); })()
          );
          break;

        case 'company':
          response = await CompanyAnalyzerService.researchCompany(
            analysisData.companyName || analysisData.text || '',
            analysisData.files || [],
            analysisData.userId || (() => { throw new Error('userId is required for analysis'); })()
          );
          break;

        default:
          throw new Error(`Unknown analyzer type: ${type}`);
      }

      // Check if request was cancelled
      if (signal.aborted) {
        logger.info('[useAnalysisOperations]', 'Analysis was cancelled');
        return null;
      }

      logger.info('[useAnalysisOperations]', 'Analysis response:', response);

      // Clear the abort controller since request completed successfully
      abortControllerRef.current = null;

      // The new services return data directly, not a request_id
      setResult(response);
      setAnalyzing(false);
      if (onComplete) {
        onComplete(response);
      }
      return response;
    } catch (error) {
      // Don't log error if it was cancelled
      if (signal.aborted) {
        logger.info('[useAnalysisOperations]', 'Analysis request was aborted');
        return null;
      }

      logger.error('[useAnalysisOperations]', 'Error starting analysis:', error);
      setError(error.message || 'Failed to start analysis');
      setAnalyzing(false);
      abortControllerRef.current = null;
      return null;
    }
  }, [type, cancelAnalysis, onComplete]);

  /**
   * Poll for request status
   */
  const pollRequestStatus = useCallback(async (id) => {
    logger.info('[useAnalysisOperations]', `Polling status for request: ${id}`);

    // Since the new services return data directly, this method is no longer needed
    // but we'll keep it for compatibility
    return { completed: true, result: null };
  }, []);

  /**
   * Process successful analysis result
   */
  const processAnalysisResult = useCallback((analysisResult) => {
    logger.info('[useAnalysisOperations]', `Processing ${type} result`);

    setResult(analysisResult);
    setActiveStep(1);
    setCompletedSteps([0]);
    setAnalyzing(false);

    // Notify parent component
    if (onComplete) {
      onComplete(analysisResult);
    }

    if (onStatusChange) {
      onStatusChange('completed');
    }
  }, [type, onComplete, onStatusChange]);

  /**
   * Handle analysis error
   */
  const handleAnalysisError = useCallback((errorMessage) => {
    logger.error('[useAnalysisOperations]', 'Analysis error:', errorMessage);

    setError(errorMessage);
    setAnalyzing(false);
    setRequestId(null);

    if (onStatusChange) {
      onStatusChange('error');
    }
  }, [onStatusChange]);

  /**
   * Reset analysis state
   */
  const resetAnalysis = useCallback(() => {
    logger.info('[useAnalysisOperations]', 'Resetting analysis state');

    setActiveStep(0);
    setCompletedSteps([]);
    setLoading(false);
    setAnalyzing(false);
    setError('');
    setSuccess('');
    setRequestId(null);
    setResult(null);
    setData({});
    setOpenEditDialog(false);
    setEditingItem(null);
    setEditingIndex(null);
  }, []);

  /**
   * Move to next step
   */
  const moveToNextStep = useCallback(() => {
    const newCompletedSteps = [...completedSteps];
    if (!newCompletedSteps.includes(activeStep)) {
      newCompletedSteps.push(activeStep);
    }
    setCompletedSteps(newCompletedSteps);
    setActiveStep(activeStep + 1);
  }, [activeStep, completedSteps]);

  /**
   * Move to previous step
   */
  const moveToPreviousStep = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  }, [activeStep]);

  /**
   * Set editing item for dialog
   */
  const startEditing = useCallback((item, index) => {
    logger.info('[useAnalysisOperations]', `Starting edit for item at index ${index}`);
    setEditingItem(item);
    setEditingIndex(index);
    setOpenEditDialog(true);
  }, []);

  /**
   * Clear editing state
   */
  const stopEditing = useCallback(() => {
    setEditingItem(null);
    setEditingIndex(null);
    setOpenEditDialog(false);
  }, []);

  return {
    // State
    activeStep,
    setActiveStep,
    completedSteps,
    setCompletedSteps,
    loading,
    setLoading,
    analyzing,
    setAnalyzing,
    error,
    setError,
    success,
    setSuccess,
    requestId,
    setRequestId,
    result,
    setResult,
    data,
    setData,
    openEditDialog,
    setOpenEditDialog,
    editingItem,
    editingIndex,

    // Operations
    startAnalysis,
    cancelAnalysis,
    pollRequestStatus,
    processAnalysisResult,
    handleAnalysisError,
    resetAnalysis,
    moveToNextStep,
    moveToPreviousStep,
    startEditing,
    stopEditing
  };
};
